import { applyPacingPreference } from "./playback";

function cacheKey(videoId, plan, mode, speedLimits, availableRates) {
  const sourceVersion = plan.generatedAt || plan.version || "plan";
  const rates = [...new Set(availableRates || [])].sort((a, b) => a - b).join(",");
  return `theofast-mode-plan:v5:${videoId}:${sourceVersion}:${mode}:${speedLimits.normal}-${speedLimits.fast}:${rates}`;
}

function restorePlan(sourcePlan, saved) {
  if (!saved || saved.segmentSpeeds?.length !== sourcePlan.segments.length || !Number.isFinite(saved.estimatedDuration)) return null;
  if (!saved.segmentSpeeds.every((speed) => Number.isFinite(speed) && speed >= 0.25 && speed <= 2)) return null;
  return {
    ...sourcePlan,
    estimatedDuration: saved.estimatedDuration,
    segments: sourcePlan.segments.map((segment, index) => ({
      ...segment,
      speed: saved.segmentSpeeds[index],
      paceCapped: Boolean(saved.paceCapped?.[index]),
    })),
  };
}

export function getModePlaybackPlan(videoId, sourcePlan, mode, speedLimits, availableRates, memoryCache = new Map(), storage) {
  if (!sourcePlan) return null;
  const key = cacheKey(videoId, sourcePlan, mode, speedLimits, availableRates);
  if (memoryCache.has(key)) return memoryCache.get(key);

  try {
    const browserStorage = storage || window.localStorage;
    const serialized = browserStorage.getItem(key);
    if (serialized) {
      const cachedPlan = restorePlan(sourcePlan, JSON.parse(serialized));
      if (cachedPlan) {
        memoryCache.set(key, cachedPlan);
        return cachedPlan;
      }
    }
  } catch { /* Private browsing or a full storage quota must not prevent playback. */ }

  const computed = applyPacingPreference(sourcePlan, speedLimits, availableRates, mode);
  memoryCache.set(key, computed);
  if (memoryCache.size > 12) memoryCache.delete(memoryCache.keys().next().value);
  if (mode !== "custom") {
    try {
      const browserStorage = storage || window.localStorage;
      browserStorage.setItem(key, JSON.stringify({
        estimatedDuration: computed.estimatedDuration,
        segmentSpeeds: computed.segments.map((segment) => segment.speed),
        paceCapped: computed.segments.map((segment) => segment.paceCapped),
      }));
    } catch { /* Playback plans still work when persistent storage is unavailable. */ }
  }
  return computed;
}
